import type { InjectionKey, Ref } from 'vue'

export interface ShortcutConfig {
    handler: Function;
    disabled?: boolean | (() => boolean); // Reactive disabled flag
    disabledByInputs?: boolean; // If this flag is true, the shortcut will be disabled when an input or textarea is focused
}

export interface ShortcutsConfig {
    [key: string]: ShortcutConfig | Function;
}

interface Shortcut {
    handler: Function;
    isEnabled: () => boolean;
    chained: boolean;
    key: string;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    scopeId: string | null;
}

const shortcutScopeStack = ref<string[]>([])

export const SHORTCUT_SCOPE_KEY = Symbol('shortcutScope') as InjectionKey<Ref<string>>

/**
 * Register a shortcut scope for modals, slide-overs, etc.
 * Shortcuts within the topmost scope take precedence over lower scopes.
 * Call this in components that create overlay UI (modals, dialogs, slide-overs).
 * 
 * @param isActive - A ref or getter that indicates if the overlay is currently visible
 */
export function useShortcutScope(isActive: Ref<boolean> | (() => boolean)): string {
    const scopeId = `scope-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    const scopeRef = ref(scopeId)

    const isActiveValue = typeof isActive === 'function' ? computed(isActive) : isActive

    watch(isActiveValue, (active) => {
        if (active) {
            shortcutScopeStack.value.push(scopeId)
        } else {
            const index = shortcutScopeStack.value.indexOf(scopeId)
            if (index > -1) {
                shortcutScopeStack.value.splice(index, 1)
            }
        }
    }, { immediate: true })

    onUnmounted(() => {
        const index = shortcutScopeStack.value.indexOf(scopeId)
        if (index > -1) {
            shortcutScopeStack.value.splice(index, 1)
        }
    })

    provide(SHORTCUT_SCOPE_KEY, scopeRef)

    return scopeId
}

export function getActiveShortcutScope(): string | null {
    if (shortcutScopeStack.value.length === 0) return null
    return shortcutScopeStack.value[shortcutScopeStack.value.length - 1] ?? null
}

function shouldShortcutExecute(shortcutScopeId: string | null): boolean {
    const activeScope = getActiveShortcutScope()

    if (shortcutScopeId === null) {
        // Root-level shortcut: only execute if no overlay scopes are active
        return activeScope === null
    }

    // Scoped shortcut: only execute if this scope is the topmost active scope
    return shortcutScopeId === activeScope
}

const keyMap: Record<string, { mac: string; win: string }> = {
    meta: { mac: "⌘", win: "Ctrl" },
    ctrl: { mac: "⌃", win: "Ctrl" },
    alt: { mac: "⌥", win: "Alt" },
    shift: { mac: "⇧", win: "⇧" },
    backspace: { mac: "⌫", win: "⌫" },
    escape: { mac: "⎋", win: "Esc" },
    delete: { mac: "⌦", win: "⌦" },
    enter: { mac: "↵", win: "↵" },
    tab: { mac: "⇥", win: "⇥" },
    arrowup: { mac: "↑", win: "↑" },
    arrowright: { mac: "→", win: "→" },
    arrowdown: { mac: "↓", win: "↓" },
    arrowleft: { mac: "←", win: "←" },
    pageup: { mac: "⇞", win: "⇞" },
    pagedown: { mac: "⇟", win: "⇟" },
    home: { mac: "↖", win: "Pos1" },
    end: { mac: "↘", win: "End" },
}

const detectMac = () => typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent)

export function isMobileDevice() {
    if (typeof navigator === 'undefined') return false
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

export function useKeymap(shortcut: string) {
    if (!useFeature('shortcuts') || isMobileDevice()) return ""
    const platform = computed(() => (detectMac() ? "mac" : "win"))
    return shortcut
        .split("_")
        .map((part) => keyMap[part]?.[platform.value] || part.toUpperCase())
        .join(platform.value === "mac" ? "" : "+")
}

function inputIsFocused() {
    const el = useActiveElement().value
    return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.contentEditable === "true")
}

function evaluateDisabled(val: any): boolean {
    if (typeof val === "function") return !!val()
    if (val?.value !== undefined) return !!val.value
    return !!val
}

function parseShortcut(key: string, cfg: ShortcutConfig | Function, macOS: boolean, scopeId: string | null): Shortcut | null {
    if (!cfg) return null
    const isChained = key.includes("-")
    const keyLower = key.toLowerCase()
    let shortcut: Partial<Shortcut>

    if (isChained) {
        shortcut = {
            key: keyLower,
            metaKey: false,
            ctrlKey: false,
            shiftKey: false,
            altKey: false,
        }
    } else {
        const parts = keyLower.split("_")
        shortcut = {
            key: parts.filter((p) => !["meta", "ctrl", "shift", "alt"].includes(p)).join("_"),
            metaKey: parts.includes("meta"),
            ctrlKey: parts.includes("ctrl"),
            shiftKey: parts.includes("shift"),
            altKey: parts.includes("alt"),
        }
        if (!macOS && shortcut.metaKey && !shortcut.ctrlKey) {
            shortcut.metaKey = false
            shortcut.ctrlKey = true
        }
    }

    shortcut.chained = isChained
    shortcut.scopeId = scopeId

    const shortcutCfg = typeof cfg === "function" ? { handler: cfg } : cfg
    if (!shortcutCfg.handler) return null

    shortcut.handler = shortcutCfg.handler
    shortcut.isEnabled = () => {
        if (evaluateDisabled(shortcutCfg.disabled)) return false
        if (shortcutCfg.disabledByInputs && inputIsFocused()) return false
        return true
    }

    return shortcut as Shortcut
}

export function useShortcuts(config?: ShortcutsConfig) {
    if (!useFeature('shortcuts') || isMobileDevice()) {
        // Do not register shortcuts on mobile devices
        return
    }
    const macOS = computed(() => detectMac())
    const chainedInputs = ref<string[]>([])
    let shortcuts: Shortcut[] = []

    const injectedScope = inject(SHORTCUT_SCOPE_KEY, null)
    const currentScopeId = injectedScope?.value ?? null

    const clearChainedInputs = () => (chainedInputs.value = [])
    const debouncedClearChainedInputs = useDebounceFn(clearChainedInputs, 800)

    function handleKeyDown(e: KeyboardEvent) {
        if (!e.key) return

        // Early preventDefault for system shortcuts that we handle
        // This prevents the browser default before our handler runs
        // Use both e.key and e.code for matching, as Shift can alter e.key
        const key = e.key.toLowerCase()
        const codeKey = e.code?.startsWith('Key') ? e.code.substring(3).toLowerCase() : null
        const hasModifier = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
        if (hasModifier) {
            for (const s of shortcuts.filter((s) => !s.chained)) {
                const keyMatch = key === s.key || codeKey === s.key
                const modMatch =
                    e.metaKey === s.metaKey &&
                    e.ctrlKey === s.ctrlKey &&
                    e.shiftKey === s.shiftKey &&
                    e.altKey === s.altKey
                if (keyMatch && modMatch && s.isEnabled() && shouldShortcutExecute(s.scopeId)) {
                    e.preventDefault()
                    break
                }
            }
        }

        if (document.activeElement instanceof HTMLElement && document.activeElement.querySelector('button')) {
            if (!inputIsFocused() && document.activeElement.tagName !== 'BODY') {
                document.activeElement.blur()
            }
        }

        chainedInputs.value.push(e.key)

        // Chained shortcuts (eg. "g-t")
        if (chainedInputs.value.length >= 2) {
            const seqKey = chainedInputs.value.slice(-2).join("-")
            for (const s of shortcuts.filter((s) => s.chained)) {
                if (s.key === seqKey && s.isEnabled() && shouldShortcutExecute(s.scopeId)) {
                    e.preventDefault()
                    s.handler()
                    clearChainedInputs()
                    return
                }
            }
        }

        // Combo shortcuts ("meta_s", "ctrl_k", etc)
        for (const s of shortcuts.filter((s) => !s.chained)) {
            let keyMatch = false
            if (e.altKey && s.altKey) {
                // Use physical key code instead of character value for Alt combinations
                const keyCode = e.code
                let actualKey = null

                if (keyCode) {
                    if (keyCode.startsWith("Key")) {
                        actualKey = keyCode.substring(3).toLowerCase()
                    } else if (keyCode === "Period") {
                        actualKey = "."
                    } else if (keyCode === "Comma") {
                        actualKey = ","
                    } else if (keyCode === "Semicolon") {
                        actualKey = ";"
                    } else if (keyCode === "Quote") {
                        actualKey = "'"
                    } else if (keyCode === "BracketLeft") {
                        actualKey = "["
                    } else if (keyCode === "Minus") {
                        actualKey = "-"
                    } else if (keyCode === "Equal") {
                        actualKey = "="
                    } else if (keyCode === "Backslash") {
                        actualKey = "\\"
                    } else if (keyCode === "Slash") {
                        actualKey = "/"
                    } else if (keyCode.startsWith("Digit")) {
                        actualKey = keyCode.substring(5)
                    }
                }

                keyMatch = actualKey === s.key
            } else {
                keyMatch = e.key.toLowerCase() === s.key
            }

            const modMatch =
                e.metaKey === s.metaKey &&
                e.ctrlKey === s.ctrlKey &&
                e.shiftKey === s.shiftKey &&
                e.altKey === s.altKey

            if (keyMatch && modMatch && s.isEnabled() && shouldShortcutExecute(s.scopeId)) {
                e.preventDefault()
                s.handler()
                clearChainedInputs()
                return
            }
        }

        debouncedClearChainedInputs()
    }

    if (config) {
        shortcuts = Object.entries(config)
            .map(([key, cfg]) => parseShortcut(key, cfg, macOS.value, currentScopeId))
            .filter(Boolean) as Shortcut[]

        useEventListener("keydown", handleKeyDown, { capture: true })
    }
}
